import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { Grid, Row, PortalButton, Modal } from '@custom-components';
import { ConnectorTypeContainer } from '@containers-admin/Connector/ConnectionType';
import { ConnectorProviderContainer } from '@containers-admin/Connector/ConnectionProvider';
import ErrorModal from '../../../Buildings/ErrorModal/ErrorModal';
import PageSEO from '../../../../components/PageSEO';
import ProgressBar from './StepsPage/ProgressBar';
import ConnectionType from './StepsPage/ConnectionType';
import ConnectionProvider from './StepsPage/ConnectionProvider';
import ConnectionAttributes from './StepsPage/ConnectionAttributes';
import ConnectionReview from './StepsPage/ConnectionReview';
import { MobileStepsContainer, Buttons } from './Steps.styles';

const allSteps = ['Connection Type', 'Connection Provider', 'Connection Attributes', 'Review'];

class NewConnection extends Component {
  static propTypes = {
    stepId: PropTypes.number,
    mainTitle: PropTypes.string,
    mainForm: PropTypes.shape({
      values: {
        connectionProvider: PropTypes.string,
        connectionAttributes: PropTypes.shape({})
      }
    }),
    selectedOption: PropTypes.arrayOf(PropTypes.string),
    totalAttributes: PropTypes.number,
    openSmallModal: PropTypes.bool,
    handleChangeOption: PropTypes.func,
    handleChangeAttribute: PropTypes.func,
    handleSetStep: PropTypes.func,
    handleCloseMainModal: PropTypes.func,
    handleSubmitConnection: PropTypes.func,
    handleEditAttributeAutoComplete: PropTypes.func,
    hydrateConnectionOptions: PropTypes.func
  };

  static defaultProps = {
    stepId: 1,
    mainTitle: '',
    mainForm: {},
    selectedOption: [],
    totalAttributes: 0,
    openSmallModal: false,
    handleChangeOption: () => {},
    handleChangeAttribute: () => {},
    handleSetStep: () => {},
    handleCloseMainModal: () => {},
    handleSubmitConnection: () => {},
    handleEditAttributeAutoComplete: () => {},
    hydrateConnectionOptions: () => {}
  };

  constructor(props) {
    super(props);

    this.state = {
      creationError: ''
    };
  }

  render() {
    const {
      stepId,
      mainTitle,
      mainForm,
      selectedOption,
      totalAttributes,
      openSmallModal,
      handleChangeOption,
      handleChangeAttribute,
      handleSetStep,
      handleCloseMainModal,
      handleSubmitConnection,
      handleEditAttributeAutoComplete,
      hydrateConnectionOptions
    } = this.props;
    const { creationError } = this.state;
    const { values } = mainForm;
    const step = parseInt(stepId, 10);

    const progressItems = allSteps.map((item) => ({
      label: item,
      onClick: () => {
        handleSetStep(step + 1, selectedOption[0]);
      }
    }));

    const handleBackButton = () => {
      if (stepId === 3 && values.connectionProvider) {
        values.connectionAttributes = {};
        values.id.connectionAttributes = {};
      }

      handleSetStep(step - 1, selectedOption[0]);
    };

    const handleForwardButton = (formDetails, nextStep, option) =>
      stepId !== 4 ? handleSetStep(nextStep + 1, option) : handleSubmitConnection(formDetails.values);

    const handleDisabledPortalButton = (optionId) => {
      let disabledPortalButton = !optionId || (optionId && optionId.length < 0);

      // Check if it is the last step
      if (stepId === 4) {
        disabledPortalButton = false;
      }

      // Exception in case there is no extra configuration attributes
      if (stepId === 3 && values.connectionProvider) {
        if (
          values.connectionProvider.localeCompare('Sabre', undefined, { sensitivity: 'base' }) !== 0 &&
          values.connectionProvider.localeCompare('Hilton', undefined, { sensitivity: 'base' }) !== 0
        ) {
          disabledPortalButton = false;
        } else {
          Object.keys(values.connectionAttributes).forEach((key) => {
            // Check if there is any field empty and removes from object
            if (values.connectionAttributes[key].length <= 0) {
              delete values.connectionAttributes[key];
            }
          });

          if (totalAttributes > 0 && Object.keys(values.connectionAttributes).length === totalAttributes) {
            disabledPortalButton = false;
          }
        }
      }

      return disabledPortalButton;
    };

    return (
      <>
        <PageSEO title="LodgeLink Admin Portal - Create New Connection" />
        <Row>
          <MobileStepsContainer>
            <Grid.Container>
              <Grid.Row>
                <Grid.Col>
                  <ProgressBar
                    heading={mainTitle}
                    step={step}
                    hideBackButton
                    progressItems={progressItems}
                    subheading={
                      stepId !== 4
                        ? ''
                        : 'Please review the page completely to submit the connection. In order to make changes to the below items click on "BACK" button to go to the respective page.'
                    }
                  />
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Buttons>
                  {stepId !== 1 && (
                    <PortalButton btnTheme="primary" uppercase onClick={() => handleBackButton()}>
                      Back
                    </PortalButton>
                  )}
                  <div className="spacer" />
                  <PortalButton
                    btnTheme="primary"
                    uppercase
                    disabled={handleDisabledPortalButton(selectedOption[stepId])}
                    onClick={() => handleForwardButton(mainForm, step, selectedOption[0])}
                  >
                    {stepId === 4 ? 'Submit' : 'Next'}
                  </PortalButton>
                </Buttons>
              </Grid.Row>
            </Grid.Container>
          </MobileStepsContainer>
        </Row>
        {stepId === 1 && (
          <ConnectorTypeContainer>
            {({ data, error, loading }) => (
              <ConnectionType
                mainForm={mainForm}
                data={hydrateConnectionOptions(data.connectionType)}
                error={error.connectionError}
                loading={loading.connectionLoading}
                handleChangeOption={handleChangeOption}
              />
            )}
          </ConnectorTypeContainer>
        )}
        {stepId === 2 && (
          <ConnectorProviderContainer>
            {({ data, error, loading }) => (
              <ConnectionProvider
                mainForm={mainForm}
                data={hydrateConnectionOptions(data.connectionProvider)}
                error={error.connectionError}
                loading={loading.connectionLoading}
                handleChangeOption={handleChangeOption}
              />
            )}
          </ConnectorProviderContainer>
        )}
        {stepId === 3 && (
          <ConnectionAttributes
            mainForm={mainForm}
            handleChangeAttribute={handleChangeAttribute}
            handleEditAttributeAutoComplete={handleEditAttributeAutoComplete}
          />
        )}
        {stepId === 4 && <ConnectionReview mainForm={mainForm} />}
        <Modal
          smallModal
          openModal={openSmallModal}
          handleClose={() => {
            handleCloseMainModal(false);
          }}
          showClose={false}
          title="Are you sure you want to cancel this new Connection?"
          actions={[
            {
              label: 'Cancel',
              onClick: () => {
                handleCloseMainModal(false);
              },
              btnTheme: 'secondary'
            },
            {
              label: 'Confirm',
              onClick: () => {
                handleCloseMainModal(true);
              },
              btnTheme: 'primary'
            }
          ]}
        />
        <ErrorModal
          openModal={creationError}
          handleClose={() => {
            this.setState({ creationError: '' });
          }}
          showClose
          title="Oops,"
          subtitle="Something went wrong!"
          content="An unknown server error has occurred."
          actions={[
            {
              label: 'Close',
              onClick: () => {
                this.setState({ creationError: '' });
              },
              btnTheme: 'secondary'
            }
          ]}
        />
      </>
    );
  }
}

export default NewConnection;
